<template>
  <div class="industry-content">
    <div class="industry-header">
      <div class="industry-col">标签</div>
      <div class="industry-col">创作者</div>
      <div class="industry-col">智能体</div>
    </div>
    <div v-for="tag in filteredTags" :key="tag.name" class="tag-row">
      <div class="industry-col tag-name">
        <span class="tag-icon">🏷️</span>
        {{ tag.name }}
      </div>
      <div class="industry-col creators-col">
        <div class="creators-scroll">
          <div v-for="creator in tag.creators" :key="creator.id" class="creator-item">
            <div class="creator-avatar" :style="{ background: getAvatarColor(creator) }">
              <img v-if="creator.headImage" :src="creator.headImage" />
              <span v-else>{{ (creator.nickName || '').charAt(0) }}</span>
            </div>
            <span class="creator-name">{{ creator.nickName }}</span>
          </div>
        </div>
      </div>
      <div class="industry-col agents-col">
        <div class="agents-container">
          <div v-for="agent in filterAgents(tag.agents)" :key="agent.agentId" class="agent-card" :class="{ selected: selectedAgentId === agent.agentId }" @click.stop="goToAgentProfile(agent)">
            <div class="selected-check" v-if="selectedAgentId === agent.agentId">✓</div>
            <div class="agent-avatar" :style="{ background: agent.avatarUrl ? 'transparent' : agent.color }">
              <img v-if="agent.avatarUrl" :src="agent.avatarUrl" />
              <span v-else>{{ agent.icon }}</span>
            </div>
            <div class="agent-name">{{ agent.name }}</div>
            <div class="agent-stats">
              <span class="subscribers">{{ formatSubscribers(agent.subscribers) }}人</span>
              <span class="price">{{ agent.price > 0 ? '¥' + agent.price : '免费' }}</span>
              <span class="access-scope">{{ formatAccessScope(agent.accessScope) }}</span>
            </div>
            <div class="agent-tooltip">
              <div class="tooltip-desc">{{ agent.description || '暂无描述' }}</div>
              <button class="use-btn" @click.stop="selectAgent(agent)">立即使用</button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="loading" class="loading-state">加载中...</div>
    <div v-if="!loading && filteredTags.length === 0" class="empty-state">{{ searchText ? '未找到匹配的标签或智能体' : '暂无行业智能体' }}</div>
  </div>
</template>

<script>
import creatorApi from '@/api/creatorApi'

const AVATAR_COLORS = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E2']
const COLORS = ['#5B8FF9', '#61DDAA', '#F6BD16', '#E86452', '#6DC8EC', '#9270CA', '#FF6B9D', '#10A37F']
const ICONS = ['🤖', '💼', '📊', '✍️', '📋', '🌐', '💻', '🚀']

export default {
  name: 'IndustryContent',
  props: {
    searchText: { type: String, default: '' },
    selectedAgentId: { type: String, default: '' }
  },
  data() {
    return {
      tagList: [],
      loading: false
    }
  },
  computed: {
    filteredTags() {
      if (!this.searchText) return this.tagList
      const kw = this.searchText.toLowerCase()
      return this.tagList.filter(t => 
        (t.name || '').toLowerCase().includes(kw) ||
        (t.agents || []).some(a => 
          (a.name || '').toLowerCase().includes(kw) || 
          (a.description || '').toLowerCase().includes(kw)
        )
      )
    }
  },
  mounted() {
    this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        const isLoggedIn = this.$store.getters.isLoggedIn
        const data = await creatorApi.getAgentsByTag(!isLoggedIn)
        this.tagList = (data || []).map(tag => ({
          ...tag,
          agents: (tag.agents || []).map((agent, i) => ({
            ...agent,
            icon: agent.icon || ICONS[i % ICONS.length],
            color: agent.color || COLORS[i % COLORS.length]
          }))
        }))
      } catch (e) {
        console.error('加载行业智能体失败:', e)
        this.tagList = []
      } finally {
        this.loading = false
      }
    },
    filterAgents(agents) {
      if (!this.searchText) return agents || []
      const kw = this.searchText.toLowerCase()
      return (agents || []).filter(a => 
        (a.name || '').toLowerCase().includes(kw) || 
        (a.description || '').toLowerCase().includes(kw)
      )
    },
    selectAgent(agent) {
      const isLoggedIn = this.$store.getters.isLoggedIn
      if (!isLoggedIn) {
        this.goToAgentProfile(agent)
      } else {
        this.$emit('select-agent', agent)
      }
    },
    getAvatarColor(creator) {
      return AVATAR_COLORS[(creator.id || 0) % AVATAR_COLORS.length]
    },
    goToAgentProfile(agent) {
      if (agent.agentId) {
        this.$router.push(`/agent/profile/a/${agent.agentId}`);
      }
    },
    formatSubscribers(count) {
      if (!count) return 0
      if (count >= 10000) return (count / 10000).toFixed(1) + '万'
      if (count >= 1000) return (count / 1000).toFixed(1) + 'k'
      return count
    },
    formatAccessScope(scope) {
      const map = { PUBLIC: '公开', FRIENDS: '好友', GROUP_MEMBERS: '群成员' }
      return map[scope] || '公开'
    }
  }
}
</script>

<style lang="scss" scoped>
.industry-content {
  background: white;
  border-radius: 8px;
  border: 1px solid #e5e5e5;
}
.industry-header {
  display: grid;
  grid-template-columns: 100px 160px 1fr;
  gap: 16px;
  padding: 12px 16px;
  background: #f8f9fa;
  border-bottom: 2px solid #e5e5e5;
  font-weight: 600;
  font-size: 14px;
  color: #333;
}
.tag-row {
  display: grid;
  grid-template-columns: 100px 160px 1fr;
  gap: 16px;
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  align-items: center;
  overflow: visible;
  position: relative;
  z-index: 2;
  &:last-child { border-bottom: none; }
  &:hover { background-color: #fafafa; z-index: 100; }
}
.industry-col { font-size: 14px; color: #666; }
.tag-name {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
  color: #333;
  .tag-icon { font-size: 14px; }
}
.creators-col { overflow: hidden; display: flex; align-items: center; position: relative; }
.creators-scroll {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding: 4px 10px 4px 0;
  width: 100%;
  &::-webkit-scrollbar { display: none; }
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.creators-col::after {
  content: '';
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 30px;
  background: linear-gradient(to right, transparent, white);
  pointer-events: none;
}
.creator-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}
.creator-avatar {
  width: 32px; height: 32px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  color: white; font-size: 14px; font-weight: 600;
  overflow: hidden;
  img { width: 100%; height: 100%; object-fit: cover; }
}
.creator-name { font-size: 10px; color: #666; white-space: nowrap; }
.agents-col { overflow: visible; }
.agents-container {
  display: flex; flex-wrap: wrap; gap: 8px;
  padding: 4px; background: #f8f9fa; border-radius: 6px;
  overflow: visible;
}
.agent-card {
  display: flex; flex-direction: column; align-items: center; gap: 4px;
  padding: 6px 8px; background: white; border-radius: 6px; border: 1px solid #e5e5e5;
  transition: all 0.2s; cursor: pointer; position: relative;
  &:hover { transform: translateY(-2px); box-shadow: 0 2px 8px rgba(0,0,0,0.1); z-index: 1001; }
  &:hover .agent-tooltip, .agent-tooltip:hover { display: flex; }
  &.selected {
    border-color: #667eea;
    box-shadow: 0 0 0 2px rgba(102,126,234,0.2);
  }
}
.selected-check {
  position: absolute; top: -8px; right: -8px;
  width: 20px; height: 20px; border-radius: 50%;
  background: #667eea; color: white;
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: bold; z-index: 10;
}
.agent-avatar {
  width: 36px !important; height: 36px !important; border-radius: 50%;
  display: flex; align-items: center; justify-content: center; font-size: 16px;
  overflow: hidden;
  img { width: 100%; height: 100%; object-fit: cover; }
}
.agent-name { font-size: 11px; color: #666; white-space: nowrap; }
.agent-stats {
  display: flex; gap: 6px; font-size: 10px; color: #999;
  .subscribers { color: #666; }
  .price { color: #ff6b6b; font-weight: 500; }
  .access-scope { color: #52c41a; }
}
.agent-tooltip {
  display: none; flex-direction: column; gap: 8px;
  position: absolute; bottom: calc(100% + 8px); left: 50%; transform: translateX(-50%);
  background: white; padding: 12px; border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.15);
  min-width: 180px; max-width: 280px; z-index: 1000;
  border: 1px solid #e5e5e5;
  &::before {
    content: ''; position: absolute; top: 100%; left: 0; right: 0;
    height: 12px;
  }
  &::after {
    content: ''; position: absolute; top: 100%; left: 50%; transform: translateX(-50%);
    border: 6px solid transparent; border-top-color: white;
  }
  .tooltip-desc {
    font-size: 12px; color: #666; line-height: 1.5; text-align: left;
  }
  .use-btn {
    background: none; border: none; padding: 0;
    color: #667eea; font-size: 12px; cursor: pointer;
    text-align: right;
    &:hover { color: #764ba2; text-decoration: underline; }
  }
}
.loading-state, .empty-state { padding: 40px; text-align: center; color: #999; }
</style>
